/**
 * Phase 15.6M — Collaborator avatar chips.
 *
 * Renders a horizontal stack of clickable collaborator avatars + names
 * for a track. Click → /artist/{user_id}.
 *
 * Phase 15.6P fix: This component is frequently rendered INSIDE a parent
 * <Link> wrapper (FeaturedPushCard / TrendingTrackCard / NewDropCard / chart
 * row). Wrapping the avatar in <Link> would produce nested <a> tags which is
 * invalid HTML and breaks React 18 hydration. We use <button> + navigate()
 * + stopPropagation so the chip stays interactive without breaking the
 * parent anchor.
 */
import { useNavigate } from "react-router-dom";
import { optimizeImage } from "@/lib/mediaUrls";

const CollaboratorChips = ({
  featuredArtists = [],
  primaryUserId = null,
  primaryArtistName = null,
  size = "sm", // "sm" | "md"
  maxVisible = 3,
  showWith = false,
  className = "",
  testIdSuffix = "",
}) => {
  const navigate = useNavigate();
  if (!Array.isArray(featuredArtists) || featuredArtists.length === 0) return null;

  const visible = featuredArtists.slice(0, maxVisible);
  const overflow = featuredArtists.length - visible.length;

  const avSize = size === "md" ? "w-7 h-7" : "w-5 h-5";
  const ringSize = size === "md" ? "ring-2" : "ring-1";

  const handleNav = (userId) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (userId) navigate(`/artist/${userId}`);
  };

  return (
    <div
      className={`flex items-center gap-1.5 ${className}`}
      data-testid={`collab-chips${testIdSuffix ? `-${testIdSuffix}` : ""}`}
    >
      {showWith && primaryArtistName && (
        <span className="text-[11px] text-purple-200/70 mr-0.5">with</span>
      )}
      {/* Avatar stack */}
      <div className="flex -space-x-1.5">
        {visible.map((f, i) => {
          const name = f.artist_name || f.name || "Artist";
          const initial = name.charAt(0).toUpperCase();
          const clickable = !!f.user_id;
          const Tag = clickable ? "button" : "span";
          const props = clickable
            ? { type: "button", onClick: handleNav(f.user_id) }
            : {};
          return (
            <Tag
              key={`${f.user_id || name}-${i}`}
              {...props}
              className={`block ${avSize} rounded-full overflow-hidden ${ringSize} ring-[#0a0a0f] bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center flex-shrink-0 ${clickable ? "hover:scale-110 cursor-pointer" : ""} transition-transform`}
              title={name}
              data-testid={`collab-avatar-${f.user_id || name.replace(/\s+/g, "-").toLowerCase()}`}
            >
              {f.profile_photo ? (
                <img src={optimizeImage(f.profile_photo, 'avatar')} alt={name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
              ) : (
                <span className="text-white text-[9px] font-bold">{initial}</span>
              )}
            </Tag>
          );
        })}
        {overflow > 0 && (
          <span
            className={`${avSize} rounded-full ${ringSize} ring-[#0a0a0f] bg-purple-900/60 text-purple-200 text-[9px] font-bold flex items-center justify-center flex-shrink-0`}
            title={`+${overflow} more`}
            data-testid="collab-avatar-overflow"
          >
            +{overflow}
          </span>
        )}
      </div>
      {/* Inline name (first collaborator) — only when stack is small */}
      {visible.length <= 2 && (
        <span className="text-[11px] text-purple-200/80 truncate flex-1 min-w-0">
          {visible.map((f, i) => {
            const name = f.artist_name || f.name || "Artist";
            if (f.user_id) {
              return (
                <button
                  key={`${f.user_id}-name-${i}`}
                  type="button"
                  onClick={handleNav(f.user_id)}
                  className="hover:text-pink-300 hover:underline bg-transparent border-0 p-0 m-0 cursor-pointer text-inherit"
                  data-testid={`collab-name-${f.user_id}`}
                >
                  {name}{i < visible.length - 1 ? ", " : ""}
                </button>
              );
            }
            return <span key={`${name}-${i}`}>{name}{i < visible.length - 1 ? ", " : ""}</span>;
          })}
          {overflow > 0 && <span className="text-purple-300/60"> +{overflow}</span>}
        </span>
      )}
    </div>
  );
};

export default CollaboratorChips;
